import { ShieldCheck } from "lucide-react";

interface Props {
  /** Optionaler Überschrifts-Block über den Badges. Wenn null/undefined → Badges erscheinen ohne Header. */
  heading?: {
    eyebrow?: string;
    title: string;
    subtitle?: string;
  };
  /** "section" (default) inkl. py-Padding & container, oder "inline" für Einbettung in andere Sections. */
  variant?: "section" | "inline";
  /** Optionaler Hintergrund-Token für die Sektion (Tailwind-Klasse). Default: bg-background. */
  bgClassName?: string;
}

/**
 * Drei Garantie-Trust-Badges, die wir an mehreren Stellen einsetzen:
 * Startseite (zwischen "Mehr als nur PV" und "Aktuelle Lage"), Anlage-Konfigurieren-
 * Seite und Standortseiten. Inhalte exakt so wie vom Kunden vorgegeben.
 */
const GarantieBadges = ({ heading, variant = "section", bgClassName = "bg-background" }: Props) => {
  const badges = [
    {
      headline: "30 Jahre",
      label: "Modul-Garantie",
      description: "Hersteller-Standard für Premium-Module (z. B. AIKO).",
    },
    {
      headline: "10–15 Jahre",
      label: "Wechselrichter-Garantie",
      description: "10 Jahre Standard, erweiterbar auf 15 Jahre bei Sigenergy.",
    },
    {
      headline: "Hersteller-Standard",
      label: "Speicher-Garantie",
      description: "Garantie laut Vorgaben des jeweiligen Speicher-Herstellers (BYD, Sigenergy).",
    },
  ];

  const content = (
    <div className="grid sm:grid-cols-3 gap-4 md:gap-6">
      {badges.map((b) => (
        <div
          key={b.label}
          className="bg-card border border-border rounded-2xl p-5 md:p-6 text-center hover:border-primary/60 transition-colors"
        >
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary/15 text-primary mb-3">
            <ShieldCheck size={24} />
          </div>
          <p className="text-2xl md:text-3xl font-bold text-foreground leading-tight">
            {b.headline}
          </p>
          <p className="text-xs uppercase tracking-[0.15em] text-primary font-semibold mt-1 mb-3">
            {b.label}
          </p>
          <p className="text-sm text-muted-foreground leading-snug">{b.description}</p>
        </div>
      ))}
    </div>
  );

  if (variant === "inline") {
    return (
      <div>
        {heading && (
          <div className="mb-6 md:mb-8 text-center">
            {heading.eyebrow && (
              <p className="eyebrow mb-2">{heading.eyebrow}</p>
            )}
            <h2 className="text-2xl md:text-3xl mb-2 leading-tight">{heading.title}</h2>
            {heading.subtitle && (
              <p className="text-sm md:text-base text-muted-foreground">{heading.subtitle}</p>
            )}
          </div>
        )}
        {content}
      </div>
    );
  }

  return (
    <section className={`pt-2 pb-8 md:pt-4 md:pb-12 ${bgClassName}`}>
      <div className="container-wm max-w-5xl">
        {heading ? (
          <div className="mb-5 md:mb-7 text-center max-w-2xl mx-auto">
            {heading.eyebrow && (
              <p className="eyebrow mb-2">{heading.eyebrow}</p>
            )}
            <h2 className="text-2xl md:text-3xl mb-2 leading-tight">{heading.title}</h2>
            {heading.subtitle && (
              <p className="text-sm md:text-base text-muted-foreground">{heading.subtitle}</p>
            )}
          </div>
        ) : null}
        {content}
      </div>
    </section>
  );
};

export default GarantieBadges;
